import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Chef } from '@/types/chef';
import useMediaQuery from '@/hooks/useMediaQuery';

interface ChefListProps {
  chefs: Chef[];
  selectedPersona: string | null;
  onSelectPersona: (id: string) => void;
}

const ChefList: React.FC<ChefListProps> = ({ chefs, selectedPersona, onSelectPersona }) => {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to selected chef on mobile
  useEffect(() => {
    if (!isDesktop && selectedPersona && scrollRef.current) {
      const selectedIndex = chefs.findIndex(c => c.id === selectedPersona);
      if (selectedIndex >= 0) {
        const cardWidth = 160; // approximate width + gap
        scrollRef.current.scrollTo({
          left: selectedIndex * cardWidth - 40,
          behavior: 'smooth'
        });
      }
    }
  }, [selectedPersona, isDesktop, chefs]);

  // Desktop: Vertical list
  if (isDesktop) {
    return (
      <div className="grid grid-cols-1 gap-3">
        {chefs.map((chef, index) => (
          <motion.button
            key={chef.id}
            onClick={() => onSelectPersona(chef.id)}
            className={cn(
              'flex items-center w-full p-3 space-x-3 text-left transition-all duration-300 ease-in-out rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400',
              selectedPersona === chef.id
                ? 'bg-white dark:bg-gray-700 ring-2 ring-orange-500 shadow-lg'
                : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-700'
            )}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex-shrink-0">
              <div
                className={cn(
                  "w-16 h-16 rounded-full border-2 bg-white",
                  selectedPersona === chef.id 
                    ? "border-orange-400 shadow-md" 
                    : "border-gray-200 dark:border-gray-600"
                )}
                style={{
                  backgroundImage: `url(${chef.avatar})`,
                  backgroundSize: '140%',
                  backgroundPosition: 'center 25%',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white truncate">{chef.name}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{chef.description}</p>
            </div>
          </motion.button>
        ))}
      </div>
    );
  }

  // Mobile: Horizontal scrollable carousel
  return (
    <div className="space-y-3">
      <p className="text-center text-xs text-gray-500 dark:text-gray-400">
        Swipe to explore • Tap to select
      </p>
      <div 
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-4 px-2 snap-x snap-mandatory scrollbar-hide"
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {chefs.map((chef, index) => (
          <motion.button
            key={chef.id}
            onClick={() => onSelectPersona(chef.id)}
            className={cn(
              'flex-shrink-0 w-36 flex flex-col items-center p-3 rounded-xl transition-all duration-300 snap-center',
              selectedPersona === chef.id
                ? 'bg-gradient-to-b from-orange-50 to-pink-50 dark:from-gray-700 dark:to-gray-800 ring-2 ring-orange-500 shadow-lg scale-105'
                : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm'
            )}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Chef Avatar */}
            <div className="relative mb-2">
              <div
                className={cn(
                  "w-20 h-20 rounded-full border-3 bg-white shadow-md overflow-hidden relative",
                  selectedPersona === chef.id 
                    ? "border-orange-400 ring-4 ring-orange-100 dark:ring-orange-900/30" 
                    : "border-gray-200 dark:border-gray-600"
                )}
              >
                {/* Video Layer - Only plays when selected */}
                {chef.video && selectedPersona === chef.id && (
                  <video
                    src={chef.video}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                )}
                
                {/* Image Layer */}
                <div 
                   className="w-full h-full transition-opacity duration-500"
                   style={{
                    backgroundImage: `url(${chef.avatar})`,
                    backgroundSize: '140%',
                    backgroundPosition: 'center 25%',
                    backgroundRepeat: 'no-repeat',
                    opacity: (chef.video && selectedPersona === chef.id) ? 0 : 1
                  }}
                />
              </div>
              {selectedPersona === chef.id && (
                <motion.div 
                  className="absolute -bottom-1 -right-1 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center shadow-md"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                >
                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
              )}
            </div>
            
            {/* Chef Name */}
            <h3 className={cn(
              "text-sm font-semibold text-center leading-tight",
              selectedPersona === chef.id 
                ? "text-orange-700 dark:text-orange-300" 
                : "text-gray-800 dark:text-white"
            )}>
              {chef.name.replace('The ', '')}
            </h3>
            
            {/* Short tagline */}
            <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-1 line-clamp-2 leading-tight">
              {chef.description.split('.')[0]}
            </p>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default ChefList;
